import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useState, type FormEvent } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import { TextField } from "@navi-soft/ui/text-field";
import { StatusBadge } from "@navi-soft/ui/status-badge";
import {
  ForumPageChrome,
  ForumPageShell,
} from "@/components/forum-page-chrome";
import {
  addCartLine,
  fetchCart,
  fetchIntakes,
  fetchMe,
  lookupPart,
  removeCartLine,
  submitCart,
  type IntakeItem,
  type OfferItem,
  type PartMeta,
} from "@/lib/api";
import { useFormatInstantDisplay } from "@/lib/format-datetime";
import { humanizeHint } from "@/lib/humanize-hint";

export const Route = createFileRoute("/parts")({
  validateSearch: (search: Record<string, unknown>): { pn?: string } => ({
    pn: typeof search.pn === "string" ? search.pn : undefined,
  }),
  component: PartsOrdersPage,
});

function PartsOrdersPage() {
  const t = useTranslations("app");
  const navigate = useNavigate({ from: "/parts" });
  const { pn: urlPn } = Route.useSearch();
  const [q, setQ] = useState(urlPn ?? "");
  const [submitted, setSubmitted] = useState((urlPn ?? "").trim());

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const res = await fetchMe();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
  const authenticated = Boolean(meQuery.data?.authenticated);

  const partQuery = useQuery({
    queryKey: ["parts", "lookup", submitted],
    enabled: authenticated && submitted.length >= 2,
    queryFn: async () => {
      const res = await lookupPart(submitted);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next = q.trim();
    setSubmitted(next);
    void navigate({ search: next ? { pn: next } : {} });
  }

  function openPn(pn: string) {
    setQ(pn);
    setSubmitted(pn);
    void navigate({ search: { pn } });
  }

  const showResults = submitted.length >= 2;
  const data = partQuery.data;
  const hasCard = Boolean(data?.part);
  const hasMatches = (data?.matches.length ?? 0) > 0;

  return (
    <ForumPageShell>
      <ForumPageChrome
        eyebrow={t("partsEyebrow")}
        title={t("navParts")}
        intro={t("partsIntro")}
      />

      {!authenticated ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("homeNeedLogin")}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="flex min-w-0 flex-col gap-4">
            <form
              onSubmit={onSubmit}
              className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] p-4 shadow-subtle"
            >
              <label className="mb-2 block text-ui-nano font-semibold uppercase tracking-[0.12em] text-[var(--brand-navy)]">
                {t("partsQueryLabel")}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <TextField
                  size="md"
                  className="min-w-0 flex-1"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("partsQueryPlaceholder")}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  className="shrink-0 sm:min-w-28"
                  disabled={q.trim().length < 2}
                >
                  <MagnifyingGlass className="mr-1.5 size-3.5" weight="bold" />
                  {t("partsSubmit")}
                </Button>
              </div>
              <p className="mt-2 text-ui-micro text-[var(--brand-text-muted)]">
                {t("partsHint")}
              </p>
            </form>

            {!showResults ? (
              <p className="text-center text-ui-dense text-[var(--brand-text-muted)] lg:text-left">
                {t("partsIdle")}
              </p>
            ) : partQuery.isLoading ? (
              <p className="text-ui-dense text-[var(--brand-text-muted)]">
                {t("loading")}
              </p>
            ) : partQuery.isError ? (
              <p className="text-ui-dense text-[var(--brand-text-muted)]">
                {t("partsError")}
              </p>
            ) : hasCard && data?.part ? (
              <PartCard part={data.part} offers={data.offers} />
            ) : hasMatches ? (
              <MatchesList matches={data!.matches} onPick={openPn} />
            ) : (
              <p className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-4 py-6 text-center text-ui-dense text-[var(--brand-text-muted)] shadow-subtle">
                {t("partsEmpty")}
              </p>
            )}
          </div>

          <CartOrdersPanel />
        </div>
      )}
    </ForumPageShell>
  );
}

function MatchesList({
  matches,
  onPick,
}: {
  matches: PartMeta[];
  onPick: (pn: string) => void;
}) {
  const t = useTranslations("app");
  return (
    <div className="flex flex-col gap-2">
      <p className="text-ui-micro text-[var(--brand-text-muted)]">
        {t("partsMatchesHint", { count: matches.length })}
      </p>
      <ul className="flex flex-col gap-2">
        {matches.map((m) => (
          <li key={m.part_number}>
            <button
              type="button"
              onClick={() => onPick(m.part_number)}
              className="flex w-full flex-col gap-0.5 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] px-4 py-3 text-left outline-none shadow-subtle hover:border-[color-mix(in_srgb,var(--brand-navy)_40%,var(--brand-border))] focus-visible:ring-2 focus-visible:ring-[rgb(var(--brand-navy-rgb)/0.3)]"
            >
              <span className="font-mono text-ui-dense font-semibold text-[var(--brand-navy)]">
                {m.part_number}
              </span>
              <span className="text-ui-micro text-[var(--brand-text-muted)]">
                {m.name || "—"}
                {m.mg != null || m.sg != null
                  ? ` · MG ${m.mg ?? "—"} / SG ${m.sg ?? "—"}`
                  : ""}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PartCard({ part, offers }: { part: PartMeta; offers: OfferItem[] }) {
  const t = useTranslations("app");
  const [hint, setHint] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: async (offer: OfferItem) => {
      const res = await addCartLine({
        part_number: offer.part_number,
        system_name: offer.system_name,
        qty: 1,
      });
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async () => {
      setHint(t("catalogAdded"));
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (err: Error) => {
      setHint(
        err.message === "trade_required"
          ? t("catalogTradeRequired")
          : t("catalogAddFailed"),
      );
    },
  });

  function stockLabel(band: string | null | undefined): string {
    if (!band) return "—";
    if (band === "in_stock") return t("stockIn");
    if (band === "low") return t("stockLow");
    if (band === "out") return t("stockOut");
    return band;
  }

  return (
    <article className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
      <div className="flex items-center justify-between gap-3 bg-[var(--brand-navy)] px-4 py-2">
        <span className="text-ui-nano font-semibold uppercase tracking-[0.12em] text-[var(--brand-bg-page)]">
          {t("partsCardBadge")}
        </span>
        {part.lifecycle_state ? (
          <span className="font-mono text-ui-micro text-[color-mix(in_srgb,var(--brand-bg-page)_70%,transparent)]">
            {part.lifecycle_state}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 px-4 py-4">
        <div>
          <h2 className="font-heading font-mono text-[17px] font-semibold tracking-tight text-[var(--brand-navy)]">
            {part.part_number}
          </h2>
          <p className="mt-1 text-ui-dense text-[var(--brand-text-muted)]">
            {part.name || t("partsNameUnknown")}
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-[var(--brand-border)] bg-[var(--brand-border)] sm:grid-cols-4">
            <Stat label={t("partsStatMg")} value={part.mg ?? "—"} />
            <Stat label={t("partsStatSg")} value={part.sg ?? "—"} />
            <Stat label={t("partsStatType")} value={part.part_type || "—"} />
            <Stat label={t("partsStatOffers")} value={offers.length} />
          </dl>
        </div>

        <div>
          <p className="mb-2 text-ui-nano font-semibold uppercase tracking-[0.12em] text-[var(--brand-navy)]">
            {t("partsOffersTitle")}
          </p>
          {offers.length === 0 ? (
            <p className="text-ui-dense text-[var(--brand-text-muted)]">
              {t("partsOffersEmpty")}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {offers.map((offer) => (
                <li
                  key={`${offer.system_name}:${offer.part_number}`}
                  className="rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg-section)] px-3 py-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-ui-nano uppercase tracking-wide text-[var(--brand-text-muted)]">
                        {t("findYard")}
                      </p>
                      <p className="font-heading text-ui-md font-semibold text-[var(--brand-navy)]">
                        {humanizeHint(offer.system_name)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={
                        !offer.can_add_to_cart || addMutation.isPending
                      }
                      title={
                        offer.can_add_to_cart
                          ? undefined
                          : t("catalogTradeRequired")
                      }
                      onClick={() => {
                        setHint(null);
                        addMutation.mutate(offer);
                      }}
                    >
                      {t("catalogAddCart")}
                    </Button>
                  </div>
                  <dl className="mt-3 grid grid-cols-3 gap-2 text-ui-micro">
                    <div>
                      <dt className="uppercase text-[var(--brand-text-muted)]">
                        {t("catalogColPrice")}
                      </dt>
                      <dd className="mt-0.5 font-mono text-[var(--brand-navy)]">
                        {offer.unit_price
                          ? `${offer.unit_price} ${offer.currency || ""}`.trim()
                          : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="uppercase text-[var(--brand-text-muted)]">
                        {t("catalogColStock")}
                      </dt>
                      <dd className="mt-0.5 text-[var(--brand-navy)]">
                        {stockLabel(offer.stock_band)}
                      </dd>
                    </div>
                    <div>
                      <dt className="uppercase text-[var(--brand-text-muted)]">
                        {t("catalogColEta")}
                      </dt>
                      <dd className="mt-0.5 text-[var(--brand-navy)]">
                        {offer.eta_hint || "—"}
                      </dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
          )}
        </div>

        {hint ? (
          <p className="text-ui-dense text-[var(--brand-text-muted)]">{hint}</p>
        ) : null}

        <p className="text-ui-micro text-[var(--brand-text-muted)]">
          {t("partsCatalogHint")}{" "}
          <Link
            to="/catalog"
            className="text-[var(--brand-navy)] underline-offset-2 hover:underline"
          >
            {t("navCatalog")}
          </Link>
        </p>
      </div>
    </article>
  );
}

function CartOrdersPanel() {
  const t = useTranslations("app");
  const formatInstant = useFormatInstantDisplay();
  const queryClient = useQueryClient();
  const [hint, setHint] = useState<string | null>(null);

  const cartQuery = useQuery({
    queryKey: ["cart"],
    queryFn: async () => {
      const res = await fetchCart();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });

  const intakesQuery = useQuery({
    queryKey: ["intakes"],
    refetchInterval: (query) => {
      const items = query.state.data;
      if (!items?.length) return false;
      const waiting = items.some((i) => i.status === "submitted");
      return waiting ? 2000 : false;
    },
    queryFn: async () => {
      const res = await fetchIntakes();
      if (!res.ok) throw new Error(res.error);
      return res.data.items;
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (lineId: string) => {
      const res = await removeCartLine(lineId);
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await submitCart();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async () => {
      setHint(t("cartSubmitted"));
      await queryClient.invalidateQueries({ queryKey: ["cart"] });
      await queryClient.invalidateQueries({ queryKey: ["intakes"] });
    },
    onError: (err: Error) => {
      setHint(
        err.message === "trade_required"
          ? t("catalogTradeRequired")
          : err.message === "cart_empty"
            ? t("cartEmpty")
            : t("cartSubmitFailed"),
      );
    },
  });

  const lines = cartQuery.data?.lines ?? [];

  return (
    <aside className="flex flex-col gap-4 lg:sticky lg:top-0 lg:self-start">
      <section className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
        <div className="flex items-center justify-between gap-2 border-b border-[var(--brand-border)] bg-[var(--brand-navy)] px-3 py-2">
          <h2 className="text-ui-nano font-semibold uppercase tracking-[0.12em] text-[var(--brand-bg-page)]">
            {t("cartTitle")}
          </h2>
          {lines.length > 0 ? (
            <span className="font-mono text-ui-micro text-[color-mix(in_srgb,var(--brand-bg-page)_70%,transparent)]">
              {lines.reduce((n, l) => n + l.qty, 0)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-2 p-3">
          <p className="text-ui-micro text-[var(--brand-text-muted)]">
            {t("cartIntro")}
          </p>
          {hint ? (
            <p className="text-ui-dense text-[var(--brand-text-muted)]">{hint}</p>
          ) : null}
          {cartQuery.isLoading ? (
            <p className="text-ui-dense text-[var(--brand-text-muted)]">
              {t("loading")}
            </p>
          ) : null}
          {cartQuery.isError ? (
            <p className="text-ui-dense text-[var(--brand-text-muted)]">
              {t("cartError")}
            </p>
          ) : null}
          {cartQuery.isSuccess && lines.length === 0 ? (
            <p className="text-ui-dense text-[var(--brand-text-muted)]">
              {t("cartEmpty")}
            </p>
          ) : null}
          {lines.length > 0 ? (
            <>
              <ul className="flex flex-col gap-2">
                {lines.map((line) => (
                  <li
                    key={line.line_id}
                    className="flex items-start justify-between gap-2 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg-section)] px-2.5 py-2"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-ui-dense font-semibold text-[var(--brand-navy)]">
                        {line.part_number}
                      </p>
                      <p className="text-ui-micro text-[var(--brand-text-muted)]">
                        {humanizeHint(line.system_name)} · ×{line.qty}
                        {line.unit_price
                          ? ` · ${line.unit_price} ${line.currency || ""}`
                          : ""}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={removeMutation.isPending}
                      onClick={() => removeMutation.mutate(line.line_id)}
                    >
                      {t("cartRemove")}
                    </Button>
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                variant="primary"
                size="md"
                className="w-full"
                disabled={submitMutation.isPending}
                onClick={() => {
                  setHint(null);
                  submitMutation.mutate();
                }}
              >
                {t("cartSubmit")}
              </Button>
            </>
          ) : null}
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
        <div className="border-b border-[var(--brand-border)] bg-[var(--brand-navy)] px-3 py-2">
          <h2 className="text-ui-nano font-semibold uppercase tracking-[0.12em] text-[var(--brand-bg-page)]">
            {t("intakesTitle")}
          </h2>
        </div>
        <div className="flex flex-col gap-2 p-3">
          <p className="text-ui-micro text-[var(--brand-text-muted)]">
            {t("intakesIntro")}
          </p>
          {intakesQuery.isLoading ? (
            <p className="text-ui-dense text-[var(--brand-text-muted)]">
              {t("loading")}
            </p>
          ) : null}
          {intakesQuery.isSuccess && intakesQuery.data.length === 0 ? (
            <p className="text-ui-dense text-[var(--brand-text-muted)]">
              {t("intakesEmpty")}
            </p>
          ) : null}
          <ul className="flex flex-col gap-2">
            {intakesQuery.data?.map((item) => (
              <IntakeCard
                key={item.intake_id}
                item={item}
                formatInstant={formatInstant}
                t={t}
              />
            ))}
          </ul>
        </div>
      </section>
    </aside>
  );
}

function intakeBadgeStatus(
  status: string,
): "active" | "warning" | "error" | "muted" {
  if (status === "accepted") return "active";
  if (status === "rejected") return "error";
  if (status === "submitted") return "warning";
  return "muted";
}

function IntakeCard({
  item,
  formatInstant,
  t,
}: {
  item: IntakeItem;
  formatInstant: (iso: string) => string;
  t: ReturnType<typeof useTranslations<"app">>;
}) {
  const waiting = item.status === "submitted";
  const lines = item.status_lines ?? [];

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg-section)] px-2.5 py-2">
      <div className="flex items-start gap-2">
        <StatusBadge
          status={intakeBadgeStatus(item.status)}
          label={
            item.status === "accepted"
              ? t("intakeStatusAccepted")
              : item.status === "rejected"
                ? t("intakeStatusRejected")
                : item.status === "submitted"
                  ? t("intakeStatusSubmitted")
                  : item.status
          }
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-ui-micro text-[var(--brand-text-muted)]">
              {humanizeHint(item.system_name)}
            </span>
            <span className="shrink-0 text-ui-micro tabular-nums text-[var(--brand-text-muted)]">
              {item.created_at ? formatInstant(item.created_at) : "—"}
            </span>
          </div>
        </div>
      </div>
      {waiting ? (
        <p className="text-ui-micro text-[var(--brand-text-muted)]">
          {t("intakeWaitingConfirm")}
        </p>
      ) : null}
      {lines.length > 0 ? (
        <ul className="flex flex-col gap-1 border-t border-[var(--brand-border)] pt-2">
          {lines.map((line, idx) => (
            <li
              key={`${item.intake_id}-${line.part_number ?? idx}`}
              className="flex flex-wrap justify-between gap-1 text-ui-micro"
            >
              <span className="font-mono font-semibold text-[var(--brand-navy)]">
                {line.part_number ?? "—"}
              </span>
              <span className="text-[var(--brand-text-muted)]">
                {line.confirmation === "qty"
                  ? t("intakeLineQty", {
                      confirmed: String(line.qty_confirmed ?? "—"),
                      requested: String(line.qty_requested ?? "—"),
                    })
                  : null}
                {line.confirmation === "eta"
                  ? t("intakeLineEta", { eta: line.promised_eta ?? "—" })
                  : null}
                {line.confirmation === "rejected"
                  ? t("intakeLineRejected", { reason: line.reason ?? "—" })
                  : null}
                {!line.confirmation ? t("intakeLinePending") : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="bg-[var(--brand-bg-section)] px-3 py-2.5">
      <dt className="text-ui-nano uppercase tracking-wide text-[var(--brand-text-muted)]">
        {label}
      </dt>
      <dd className="mt-0.5 font-heading text-ui-md font-semibold text-[var(--brand-navy)]">
        {value}
      </dd>
    </div>
  );
}
